// bot-settings.ts — zod schema for `Bot.settings` (spec D13). The whole schema is
// `.default({})`, and every leaf has its own default, so `botSettingsSchema.parse({})` (or
// `.parse(undefined)`) always yields a fully-populated `BotSettings` object — there is no
// "settings not set yet" state once a value has passed through this schema.
//
// `apps/api/src/routes/bots.ts` PATCH additionally parses with `.partial()` (see
// `packages/validation/src/bots.ts`) so a client can send only the top-level keys it wants to
// change; each *present* key still validates against its full (defaulted) sub-schema.
import { z } from 'zod';
import { WIDGET_LOCALES } from '@repo/shared-types';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export const botSettingsPositionSchema = z.enum(['bottom-right', 'bottom-left']);
export const leadCaptureModeSchema = z.enum(['off', 'on-intent', 'always', 'pre-chat']);
/** Transcript-leadlevel spec §B — only meaningful when `leadCapture === 'on-intent'`. */
export const leadProactivenessLevelSchema = z.enum(['subtle', 'balanced', 'proactive']);
/** Widget-i18n spec, Task 3 — `'auto'` plus every `WIDGET_LOCALES` entry (`@repo/shared-types`),
 * not hand-listed, so a third language only needs adding there.
 *
 * `.catch('auto')`, not `.default('auto')`: `.default` only substitutes for a MISSING value,
 * so a value that's present but no longer in the enum would still fail parsing. That gap
 * matters here in a way it doesn't for `leadCapture`/`position`: adding a locale to
 * `WIDGET_LOCALES` is safe and symmetric, but REMOVING or renaming one is not — any bot that
 * had pinned it now holds a `language` value outside the enum, and `getPublicBot`
 * (apps/api/src/app.ts) runs this schema on every public route, including chat, not just the
 * config endpoint. Without `.catch`, that one stored value 500s `GET /v1/bots/:publicId/config`
 * AND every chat request for that bot — a total outage from a locale edit, not a bad review.
 * `.catch('auto')` degrades a bad stored value to the same safe default a missing one already
 * gets, so the bot stays reachable (with 'auto' chrome) instead of going dark. This also means
 * `botSettingsSchema.parse` can no longer reject an invalid `language` on write (PATCH) the way
 * it rejects a bad hex color — accepted here as the cost of the read-path guarantee, since the
 * dashboard's own Select already only offers valid values. */
export const botLanguageSettingSchema = z.enum(['auto', ...WIDGET_LOCALES]).catch('auto');

/** Bare hostname only — no scheme, no path. `new URL()` isn't used here because a bare
 * hostname like "example.com" isn't a valid URL on its own; a substring check on the two
 * characters that could never appear in a hostname is simpler and exact for this purpose. */
const hostnameSchema = z
  .string()
  .min(1)
  .max(253)
  .refine((value) => !value.includes('://') && !value.includes('/'), {
    message:
      'allowedDomains entries must be bare hostnames, e.g. "example.com" (no scheme or path)',
  });

// NOTE on zod v4 `.default()` vs `.prefault()`: `.default(x)` substitutes `x` for a missing
// value WITHOUT re-running it through the inner schema — fine for a leaf (`color`,
// `leadCapture`, ...) but wrong for a nested *object* default, since `{}` would then surface
// as literally `{}` instead of being re-parsed into that object's own per-field defaults.
// `.prefault(x)` (pre-parse default) substitutes `x` and THEN parses it normally, so a
// completely-missing `appearance` key still expands to all of its leaf defaults. Leaf fields
// below intentionally keep `.default()` (no reparsing needed for a primitive/array default).
const appearanceObjectSchema = z.object({
  color: z
    .string()
    .regex(HEX_COLOR, 'color must be a 6-digit hex code, e.g. #4f46e5')
    .default('#4f46e5'),
  botName: z.string().max(40).default(''),
  welcome: z.string().max(300).default(''),
  quickQuestions: z.array(z.string().max(80)).max(4).default([]),
  position: botSettingsPositionSchema.default('bottom-right'),
});

export const appearanceSettingsSchema = appearanceObjectSchema.prefault({});

// Deliberately NOT wrapped in `.default({})`/`.prefault({})` at this level: `bots.ts` needs
// `.partial()` (only available on a plain `ZodObject`, not on a `ZodDefault`/`ZodPrefault`
// wrapper) so a PATCH body can supply a subset of top-level keys. `{}` still parses to full
// defaults because every field below (including `appearance`, via the `.prefault({})` above)
// has its own default/prefault — there's just no default for a wholly-`undefined` top level.
export const botSettingsSchema = z.object({
  appearance: appearanceSettingsSchema,
  leadCapture: leadCaptureModeSchema.default('on-intent'),
  leadLevel: leadProactivenessLevelSchema.default('balanced'),
  allowedDomains: z.array(hostnameSchema).default([]),
  // Badge opt-in spec (2026-08-11) — new leaf, `.default(false)` so a pre-existing settings
  // blob that predates this key entirely still parses (existing bots keep the badge as it was:
  // required on Free, absent on paid unless they'd already turned it on, which no bot could
  // have before this field existed).
  showBadge: z.boolean().default(false),
  // No `.default('auto')` needed here — `botLanguageSettingSchema`'s own `.catch('auto')`
  // already covers a missing value (undefined fails the enum parse just like an invalid one
  // does, so catch fires either way) as well as a stored value outside the current enum.
  language: botLanguageSettingSchema,
});
